<template>
    <div>
        <Head :title="__('Request Additional Information') + ticket.uid" />

        <div class="max-w-7xl mx-auto py-6 px-4 sm:px-6 lg:px-8">
            <div class="flex justify-between items-center mb-6">
                <h1 class="text-2xl font-bold text-gray-900">
                    {{ __('Request Additional Information') }} #{{ ticket.uid }}
                </h1>
                <div class="flex space-x-2">
                    <button @click="showEmailModal = true" class="btn-indigo">
                        {{ __('Send Email') }}
                    </button>
                    <Link :href="route('tickets.edit', ticket.id)" class="btn-indigo">
                    {{ __('Back to Ticket') }}
                    </Link>
                </div>
            </div>

            <!-- Email Modal -->
            <Modal :show="showEmailModal" @close="closeEmailModal" max-width="2xl">
                <div class="p-6 flex flex-col" style="max-height: 80vh;">
                    <div class="flex-shrink-0">
                        <div v-if="warning" class="bg-yellow-100 border-l-4 border-yellow-500 text-yellow-700 p-4 mb-4">
                            <p>{{ warning }}</p>
                        </div>
                        <transition name="status-fade">
                            <div
                                v-if="emailStatus.message"
                                class="mb-4 rounded-md border px-4 py-3 text-sm"
                                :class="emailStatusClass"
                            >
                                <div class="flex items-center gap-2">
                                    <span
                                        v-if="sendingEmail"
                                        class="h-4 w-4 rounded-full border-2 border-current border-t-transparent animate-spin"
                                    />
                                    <span>{{ emailStatus.message }}</span>
                                </div>
                                <div
                                    v-if="sendingEmail"
                                    class="mt-3 h-1.5 overflow-hidden rounded-full bg-black/10"
                                >
                                    <div class="email-progress-bar h-full rounded-full bg-current" />
                                </div>
                            </div>
                        </transition>
                        <h2 class="text-lg font-medium text-gray-900 mb-4">{{ __('Send Email') }}</h2>
                    </div>

                    <div class="overflow-y-auto flex-grow pr-2 -mr-2">
                        <div v-if="loadingTemplates" class="text-center py-4">
                            <span class="animate-spin">🌀</span> {{ __('Loading templates...') }}
                        </div>
                        <template v-else>
                            <div class="grid grid-cols-1 gap-4">
                                <div>
                                    <label class="block text-gray-700 text-sm font-bold mb-2">{{ __('Recipients') }}</label>
                                    <TextInput v-model="emailForm.recipients" type="text" class="w-full"
                                        :placeholder="__('email1@example.com, email2@example.com')"
                                        :readonly="hasRecipientEmail" />
                                </div>
                                <div class="mb-4">
                                    <div v-if="recipientWarning" class="mt-2 text-yellow-600 text-sm">
                                        <icon name="exclamation" class="w-4 h-4 inline mr-1" />
                                        {{ recipientWarning }}
                                    </div>
                                </div>

                                <div>
                                    <label class="block text-gray-700 text-sm font-bold mb-2">{{ __('Subject') }}</label>
                                    <TextInput v-model="emailForm.subject" type="text" class="w-full" required />
                                </div>

                                <div>
                                    <label class="form-label">{{ __('Request Details') }}</label>
                                    <textarea v-model="emailForm.message" class="w-full border border-gray-300 rounded-md p-2 focus:border-indigo-500 focus:ring focus:ring-indigo-200 focus:ring-opacity-50" required></textarea>
                                </div>

                                <div>
                                    <label class="block text-gray-700 text-sm font-bold mb-2">{{ __('Attachments') }}</label>
                                    <div class="flex items-center gap-2">
                                        <input type="file" multiple @change="handleEmailAttachment"
                                            class="form-input flex-1" accept=".pdf,.doc,.docx,.xls,.xlsx,.jpg,.jpeg,.png" />
                                    </div>
                                    <div v-if="emailForm.attachments.length" class="mt-2">
                                        <div v-for="(file, index) in emailForm.attachments" :key="index"
                                            class="flex items-center justify-between py-1">
                                            <span class="text-sm truncate">{{ file.name }}</span>
                                            <button type="button" @click="removeEmailAttachment(index)"
                                                class="text-red-500 hover:text-red-700">
                                                <svg xmlns="http://www.w3.org/2000/svg" class="h-4 w-4" fill="none"
                                                    viewBox="0 0 24 24" stroke="currentColor">
                                                    <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2"
                                                        d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                                                </svg>
                                            </button>
                                        </div>
                                    </div>
                                </div>
                            </div>
                        </template>
                    </div>

                    <div class="flex-shrink-0 flex justify-end mt-6 space-x-2 pt-4 border-t border-gray-200">
                        <button type="button" @click="closeEmailModal" class="btn-gray">
                            {{ __('Cancel') }}
                        </button>
                        <LoadingButton @click="sendEmail" :loading="sendingEmail" class="btn-indigo">
                            {{ sendingEmail ? __('Sending...') : __('Send Email') }}
                        </LoadingButton>
                    </div>
                </div>
            </Modal>
            <!-- End Email Modal -->

            <div class="bg-white shadow overflow-hidden sm:rounded-lg">
                <div class="px-4 py-5 sm:px-6 border-b border-gray-200">
                    <h3 class="text-lg leading-6 font-medium text-gray-900">
                        {{ ticket.subject }}
                    </h3>
                    <p class="mt-1 max-w-2xl text-sm text-gray-500">
                        {{ __('Created') }}: {{ formatDate(ticket.created_at) }}
                        <span v-if="ticket.closed_at" class="ml-4">
                            {{ __('Closed') }}: {{ formatDate(ticket.closed_at) }}
                        </span>
                    </p>
                </div>

                <div class="bg-white shadow overflow-hidden sm:rounded-lg mt-6">
                    <ul class="divide-y divide-gray-200">
                        <li v-for="activity in activities" :key="activity.id" class="px-4 py-4 sm:px-6">
                            <div class="flex items-center justify-between">
                                <div class="flex items-center">
                                    <div
                                        class="flex-shrink-0 h-10 w-10 rounded-full bg-gray-200 flex items-center justify-center mr-4">
                                        {{ activity.user_id ? activity.user.first_name.charAt(0) +
                                        activity.user.last_name.charAt(0)
                                        : 'S' }}
                                    </div>
                                    <div>
                                        <p class="text-sm font-medium text-gray-900">
                                            {{ activity.user_id ? activity.user.first_name + ' ' +
                                            activity.user.last_name :
                                            'System' }}
                                        </p>
                                        <p class="text-sm text-gray-500">
                                            {{ formatDate(activity.created_at) }}
                                        </p>
                                    </div>
                                </div>
                                <div class="ml-4 flex-shrink-0">
                                    <span class="px-2 inline-flex text-xs leading-5 font-semibold rounded-full"
                                        :class="activityColor(activity.description)">
                                        {{ activity.description }}
                                    </span>
                                </div>
                            </div>
                            <div class="mt-2 text-sm text-gray-700" v-if="activity.changes">
                                <div v-for="(change, field) in formatChanges(activity.changes)" :key="field"
                                    class="mb-1" v-if="change && typeof change === 'object'">
                                    <span class="font-semibold">{{ fieldLabel(field) }}:</span>
                                    <span v-if="change.old !== undefined && change.old !== null"
                                        class="text-red-600 line-through mr-1">
                                        {{ formatChangeValue(field, change.old) }}
                                    </span>
                                    <span v-if="change.new !== undefined && change.new !== null" class="text-green-600">
                                        {{ formatChangeValue(field, change.new) }}
                                    </span>
                                </div>
                            </div>
                        </li>
                    </ul>
                </div>
            </div>
        </div>
    </div>
</template>

<script>
import { Head, Link } from '@inertiajs/vue3';
import Layout from '@/Shared/Layout';
import Modal from '@/Shared/Modal';
import SelectInput from '@/Shared/SelectInput';
import TextInput from '@/Shared/TextInput';
import LoadingButton from '@/Shared/LoadingButton';
import Icon from '@/Shared/Icon';
import ClassicEditor from '@ckeditor/ckeditor5-build-classic';
import axios from 'axios';

export default {
    components: { Head, Link, Modal, SelectInput, TextInput, LoadingButton, Icon, ClassicEditor, ckeditor: ClassicEditor.component },
    layout: Layout,
    props: {
        ticket: Object,
        hasRecipientEmail: Boolean,
        defaultRecipient: String,
        warning: {
            type: String,
            default: '',
        },
        activities: Array,
        emailTemplates: {
            type: Array,
            default: () => []
        }
    },
    data() {
        return {
            form: this.$inertia.form({
                recipients: this.hasRecipientEmail ? this.defaultRecipient : '',
            }),
            showEmailModal: false,
            loadingTemplates: false,
            sendingEmail: false,

            editor: ClassicEditor,
            editorConfig: {
                // CKEditor configuration
            },
            emailForm: {
                template_id: null,
                recipients: '',
                subject: '',
                message: '',
                attachments: []
            },
            emailStatus: {
                type: '',
                message: '',
            },
        }
    },
    computed: {
        parsedRecipients() {
            return (this.emailForm.recipients || '')
                .split(/[,;]/)
                .map(email => email.trim())
                .filter(Boolean)
        },
        recipientsAreValid() {
            if (!this.parsedRecipients.length) {
                return false
            }

            return this.parsedRecipients.every(email => /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email))
        },
        recipientWarning() {
            if (this.hasRecipientEmail) {
                return ''
            }

            if (!this.emailForm.recipients) {
                return this.warning || 'Please enter a valid email address.'
            }

            if (!this.recipientsAreValid) {
                return 'Please enter a valid email address.'
            }

            return ''
        },
        emailStatusClass() {
            if (this.emailStatus.type === 'success') {
                return 'border-green-200 bg-green-50 text-green-700'
            }

            if (this.emailStatus.type === 'error') {
                return 'border-red-200 bg-red-50 text-red-700'
            }

            return 'border-blue-200 bg-blue-50 text-blue-700'
        }
    },
    methods: {
        formatDate(date) {
            return new Date(date).toLocaleString();
        },
        activityColor(description) {
            const desc = description.toLowerCase();
            if (desc.includes('closed')) return 'bg-red-100 text-red-800';
            if (desc.includes('created')) return 'bg-green-100 text-green-800';
            if (desc.includes('assigned')) return 'bg-purple-100 text-purple-800';
            if (desc.includes('status')) return 'bg-blue-100 text-blue-800';
            if (desc.includes('priority')) return 'bg-yellow-100 text-yellow-800';
            return 'bg-gray-100 text-gray-800';
        },
        fieldLabel(field) {
            if (typeof field !== 'string') {
                console.warn('Invalid field type:', field);
                return 'Field';
            }

            const labels = {
                'status_id': 'Status',
                'priority_id': 'Priority',
                'assigned_to': 'Assigned To',
                'category_id': 'Category',
                'sub_category_id': 'Subcategory',
                'department_id': 'Department',
                'due': 'Due Date',
                'resolution': 'Resolution',
                'subject': 'Subject',
                'message': 'Message'
            };

            return labels[field] || field;
        },
        formatChanges(changes) {
            try {
                if (typeof changes === 'object' && !Array.isArray(changes)) {
                    return changes;
                }
                if (typeof changes === 'string') {
                    return JSON.parse(changes);
                }
                console.warn('Unexpected changes format:', changes);
                return {};
            } catch (e) {
                console.error('Error parsing changes:', e);
                return {};
            }
        },
        closeEmailModal() {
            this.showEmailModal = false;
            this.resetEmailForm();
            this.clearEmailStatus();
        },
        resetEmailForm() {
            this.emailForm = {
                template_id: null,
                recipients: '',
                subject: '',
                message: '',
                attachments: []
            };
        },
        clearEmailStatus() {
            this.emailStatus = {
                type: '',
                message: '',
            };
        },
        applyTemplate() {
            if (!this.emailForm.template_id) return;

            const template = this.emailTemplates.find(t => t.id === this.emailForm.template_id);
            if (template) {
                this.emailForm.subject = template.subject;
                this.emailForm.message = template.content;

                // Replace placeholders in the template
                const placeholders = {
                    ticket_id: this.ticket.uid,
                    ticket_subject: this.ticket.subject,
                    customer_name: this.ticket.user ? this.ticket.user.first_name + ' ' + this.ticket.user.last_name : 'Customer',
                    agent_name: this.$page.props.auth.user.first_name + ' ' + this.$page.props.auth.user.last_name,
                    ticket_status: this.ticket.status ? this.ticket.status.name : 'N/A',
                    ticket_priority: this.ticket.priority ? this.ticket.priority.name : 'N/A',
                    ticket_created: this.formatDate(this.ticket.created_at)
                };

                for (const [key, value] of Object.entries(placeholders)) {
                    this.emailForm.subject = this.emailForm.subject.replace(new RegExp(`{{${key}}}`, 'g'), value);
                    this.emailForm.message = this.emailForm.message.replace(new RegExp(`{{${key}}}`, 'g'), value);
                }
            }
        },
        handleEmailAttachment(event) {
            const files = event.target.files;
            for (let i = 0; i < files.length; i++) {
                this.emailForm.attachments.push(files[i]);
            }
            event.target.value = '';
        },
        removeEmailAttachment(index) {
            this.emailForm.attachments.splice(index, 1);
        },
        async sendEmail() {
            if (this.sendingEmail) {
                return;
            }

            if (!this.recipientsAreValid) {
                this.emailStatus = {
                    type: 'error',
                    message: 'Not successful. Please enter a valid email address.',
                };
                this.$toast.error('Please enter a valid email address.');
                return;
            }

            this.sendingEmail = true;
            this.emailStatus = {
                type: 'progress',
                message: 'Sending email. Please wait...',
            };
            this.$toast.info('Sending email...');

            try {
                const formData = new FormData();
                formData.append('ticket_id', this.ticket.id);
                formData.append('recipients', this.emailForm.recipients);
                formData.append('subject', this.emailForm.subject);
                formData.append('message', this.emailForm.message);
                formData.append('template_id', this.emailForm.template_id || '');

                // Add attachments
                this.emailForm.attachments.forEach(file => {
                    formData.append('attachments[]', file);
                });

                await axios.post(route('tickets.ticket-email', this.ticket.id), formData, {
                    headers: {
                        'X-Requested-With': 'XMLHttpRequest',
                    },
                });

                this.emailStatus = {
                    type: 'success',
                    message: 'Successfully sent.',
                };
                this.$toast.success('Successfully sent.');
                setTimeout(() => {
                    this.closeEmailModal();
                }, 900);

            } catch (error) {
                console.error('Error sending email:', error);
                const validationErrors = error.response?.data?.errors;
                const errorMessage = error.response?.data?.message
                    || (validationErrors
                        ? Object.values(validationErrors).flat().join(' ')
                        : 'Not successful. An error occurred while sending the email.');

                this.emailStatus = {
                    type: 'error',
                    message: errorMessage,
                };
                this.$toast.error(errorMessage);
            } finally {
                this.sendingEmail = false;
            }
        },
        formatChangeValue(field, value) {
            // Handle special fields like status, priority, etc.
            if (field.endsWith('_id')) {
                const model = field.replace('_id', '');
                if (this.ticket[model] && this.ticket[model].name) {
                    return this.ticket[model].name;
                }
            }
            return value;
        }
    },
    mounted() {
        // Set default recipients if ticket has a customer email
        if (this.hasRecipientEmail && this.ticket.user && this.ticket.user.email) {
            this.emailForm.recipients = this.ticket.user.email;
        }

        // Set default subject
        this.emailForm.subject = `Regarding your ticket #${this.ticket.uid}: ${this.ticket.subject}`;
    }
}
</script>

<style scoped>
.status-fade-enter-active,
.status-fade-leave-active {
    transition: opacity 0.2s ease, transform 0.2s ease;
}

.status-fade-enter-from,
.status-fade-leave-to {
    opacity: 0;
    transform: translateY(-4px);
}

.email-progress-bar {
    width: 35%;
    animation: email-progress-slide 1.2s ease-in-out infinite;
}

@keyframes email-progress-slide {
    0% {
        transform: translateX(-120%);
    }

    100% {
        transform: translateX(320%);
    }
}
</style>
